import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaPaperPlane,
    FaReply,
    FaCheckDouble,
    FaTrash,
    FaBell,
    FaLifeRing,
    FaFilter,
    FaTimes,
    FaChevronRight,
    FaRegCommentDots,
    FaHistory,
    FaTicketAlt
} from 'react-icons/fa';
import { showSuccess, showError, showConfirm, showLoading, closeLoading } from '../../../utils/Alert';
import DataTable from '../shared/DataTable';
import Loader from '../../shared/Loader';
import './SupportAndNotifications.css';
import './DashboardOverview.css';

const SupportAndNotifications = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [targetAudience, setTargetAudience] = useState('all');
    const [isThreadModalOpen, setIsThreadModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/support/tickets', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(res.data);
            setLoading(false);
        } catch (err) {
            showError('Sync Failure', 'Could not synchronize with the Support Queue.');
            setLoading(false);
        }
    };

    const fetchTicketDetails = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/support/tickets/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedTicket(res.data);
            setIsThreadModalOpen(true);
        } catch (err) {
            showError('Thread Error', 'Failed to retrieve conversation history.');
        }
    };

    const handleSendBroadcast = async (e) => {
        e.preventDefault();
        if (!message.trim()) return showError("Validation Error", "Announcement cannot be empty.");

        const result = await showConfirm('Deploy Broadcast?', `Push notification to ${targetAudience.toUpperCase()} cluster?`);
        if (result.isConfirmed) {
            showLoading('Deploying broadcast...');
            try {
                const token = localStorage.getItem('token');
                await axios.post('/api/admin/broadcast',
                    { message, target: targetAudience },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                closeLoading();
                showSuccess("Broadcast Deployed", "The notification has been propagated throughout the platform ecosystem.");
                setMessage('');
            } catch (err) {
                closeLoading();
                showError('Broadcast Failure', 'Network error during transmission.');
            }
        }
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim()) return;

        showLoading('Transmitting Response...');
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/support/tickets/${selectedTicket._id}/reply`,
                { messageText: replyText },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setReplyText('');
            closeLoading();
            fetchTicketDetails(selectedTicket._id);
        } catch (err) {
            closeLoading();
            showError('Transmission Error', 'Failed to send response.');
        }
    };

    const handleResolve = async (id) => {
        const confirm = await showConfirm('Resolve Ticket?', 'Mark this issue as permanently resolved and lock the thread?');
        if (confirm.isConfirmed) {
            showLoading('Updating Ledger...');
            try {
                const token = localStorage.getItem('token');
                await axios.patch(`/api/support/tickets/${id}/status`,
                    { status: 'Resolved' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                closeLoading();
                showSuccess('Resolved', 'The ticket has been archived successfully.');
                fetchTickets();
                if (isThreadModalOpen) setIsThreadModalOpen(false);
            } catch (err) {
                closeLoading();
                showError('Protocol Error', 'Failed to update ticket status.');
            }
        }
    };

    const ticketColumns = [
        {
            header: 'Ticket ID', key: '_id', width: '15%', render: (row) => (
                <strong style={{ color: 'var(--primary-blue)' }}>#{row._id.substring(18).toUpperCase()}</strong>
            )
        },
        {
            header: 'Requester', key: 'creatorId', width: '20%', render: (row) => (
                <div>
                    <div style={{ color: '#0F172A', fontWeight: '600' }}>{row.creatorId?.name}</div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>{row.creatorId?.role?.toUpperCase()}</div>
                </div>
            )
        },
        {
            header: 'Subject & Tech Domain', key: 'subject', width: '30%', render: (row) => (
                <div>
                    <div style={{ color: '#0F172A', fontWeight: '500' }}>{row.subject}</div>
                    <div style={{ fontSize: '11px', color: '#64748B' }}>{row.category}</div>
                </div>
            )
        },
        {
            header: 'Priority', key: 'priority', width: '10%', render: (row) => (
                <span style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: row.priority === 'High' ? '#EF4444' : row.priority === 'Normal' ? '#F59E0B' : '#64748B'
                }}>
                    {row.priority}
                </span>
            )
        },
        {
            header: 'Status', key: 'status', width: '15%', render: (row) => (
                <span className={`status-badge ${row.status === 'Resolved' ? 'active' : 'pending'}`} style={{
                    background: row.status === 'In Progress' ? 'rgba(59, 130, 246, 0.1)' : '',
                    color: row.status === 'In Progress' ? '#3B82F6' : ''
                }}>
                    {row.status}
                </span>
            )
        },
        {
            header: 'Actions', key: 'actions', width: '10%', render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn-gold sm" title="Moderate Thread" onClick={() => fetchTicketDetails(row._id)}>
                        <FaReply />
                    </button>
                    {row.status !== 'Resolved' && (
                        <button className="action-btn-secondary sm" title="Mark Resolved" onClick={() => handleResolve(row._id)}>
                            <FaCheckDouble />
                        </button>
                    )}
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="support-notifications fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Executive Support & System Pulse</h1>
                <p className="page-subtitle">Unified command for platform-wide broadcasts and granular issue resolution.</p>
            </div>

            <div className="support-grid">
                {/* Broadcast Toggle Button + Collapsible Panel */}
                <div style={{ width: '100%' }}>
                    {/* Full-width Toggle Button */}
                    <button
                        onClick={() => setIsBroadcastOpen(prev => !prev)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '20px 32px',
                            background: isBroadcastOpen
                                ? 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)'
                                : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                            border: `1px solid ${isBroadcastOpen ? 'rgba(212, 175, 55, 0.5)' : 'rgba(212, 175, 55, 0.2)'}`,
                            borderRadius: isBroadcastOpen ? '20px 20px 0 0' : '20px',
                            cursor: 'pointer',
                            color: '#fff',
                            transition: 'all 0.3s ease',
                            boxShadow: isBroadcastOpen
                                ? '0 4px 24px rgba(212, 175, 55, 0.15)'
                                : '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{
                                width: '44px',
                                height: '44px',
                                borderRadius: '12px',
                                background: 'rgba(212, 175, 55, 0.15)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}>
                                <FaBell style={{ color: 'var(--primary-gold, #D4AF37)', fontSize: '18px' }} />
                            </div>
                            <div style={{ textAlign: 'left' }}>
                                <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-0.3px' }}>Mission Broadcast Center</div>
                                <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>Deploy high-priority alerts across specific platform sectors</div>
                            </div>
                        </div>
                        <FaChevronRight
                            style={{
                                color: 'var(--primary-gold, #D4AF37)',
                                fontSize: '16px',
                                transform: isBroadcastOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s ease'
                            }}
                        />
                    </button>

                    {/* Collapsible Broadcast Panel */}
                    {isBroadcastOpen && (
                        <div className="broadcast-panel glass-card" style={{ borderRadius: '0 0 20px 20px', borderTop: 'none', marginTop: 0 }}>
                            <form className="broadcast-form" onSubmit={handleSendBroadcast}>
                                <div className="form-group">
                                    <label className="premium-label">Target Audience Sector</label>
                                    <select
                                        value={targetAudience}
                                        onChange={(e) => setTargetAudience(e.target.value)}
                                        className="glass-input"
                                    >
                                        <option value="all">Global Ecosystem (All Users)</option>
                                        <option value="students">Academic Sector (Students)</option>
                                        <option value="tutors">Educator Sector (Tutors)</option>
                                        <option value="recruiters">Corporate Sector (Recruiters)</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Transmission Content</label>
                                    <textarea
                                        rows="6"
                                        placeholder="Draft the mission-critical announcement here..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        className="glass-input resize-none"
                                    ></textarea>
                                </div>

                                <button type="submit" className="action-btn-gold pulse-on-hover" style={{ marginTop: '12px', width: '100%', height: '48px' }}>
                                    <FaPaperPlane style={{ marginRight: '8px' }} /> Deploy Notification
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {/* Support Queue - Standardized DataTable */}
                <div className="tickets-panel no-padding">
                    <DataTable
                        title="Mission Critical Support History"
                        columns={ticketColumns}
                        data={tickets}
                        filterOptions={[
                            { label: 'Full Queue', value: '' },
                            { label: 'Open Issues', value: 'Open' },
                            { label: 'Work In Progress', value: 'In Progress' },
                            { label: 'Resolved Tickets', value: 'Resolved' }
                        ]}
                    />
                </div>
            </div>

            {/* Moderation Thread Modal - MASTERPIECE REDESIGN */}
            {isThreadModalOpen && selectedTicket && createPortal(
                <div className={`modal-overlay ${isThreadModalOpen ? 'open' : ''}`} onClick={() => setIsThreadModalOpen(false)} style={{ background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)' }}>
                    <div className="modal-content fade-in" onClick={e => e.stopPropagation()} style={{
                        maxWidth: '1000px',
                        height: '92vh',
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        borderRadius: '32px',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                        {/* Executive Command Header */}
                        <div className="modal-header" style={{
                            padding: '32px 40px',
                            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                            color: '#fff',
                            borderBottom: 'none',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: 'rgba(212, 175, 55, 0.15)',
                                    color: 'var(--primary-gold)',
                                    borderRadius: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '28px',
                                    border: '1px solid rgba(212, 175, 55, 0.3)',
                                    boxShadow: '0 0 20px rgba(212, 175, 55, 0.1)'
                                }}>
                                    <FaTicketAlt />
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', color: '#fff' }}>Mission Control: Lifecycle Moderation</h2>
                                        <span style={{
                                            background: selectedTicket.status === 'Resolved' ? '#10B981' : '#F59E0B',
                                            color: '#fff',
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 800,
                                            textTransform: 'uppercase'
                                        }}>{selectedTicket.status}</span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8', fontWeight: 500 }}>
                                        Requester: <span style={{ color: 'var(--primary-gold)' }}>{selectedTicket.creatorId?.name}</span> • Trace ID: #{selectedTicket._id.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                            <button
                                className="modal-close-btn"
                                onClick={() => setIsThreadModalOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Intelligence Strip */}
                        <div style={{ padding: '16px 40px', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px' }}>Subject Intelligence:</span>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A' }}>{selectedTicket.subject}</span>
                        </div>

                        {/* Mitigation Vault (Chat Area) */}
                        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', background: '#F8FAFC', padding: '40px' }}>
                            <div className="chat-container" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                                {selectedTicket.messages.map((msg, idx) => {
                                    const isSelf = msg.senderModel === 'super_admin';
                                    return (
                                        <div key={idx} style={{
                                            alignSelf: isSelf ? 'flex-end' : 'flex-start',
                                            maxWidth: '80%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: isSelf ? 'flex-end' : 'flex-start',
                                            animation: 'fadeInUp 0.4s ease-out forwards'
                                        }}>
                                            <div style={{
                                                background: isSelf ? 'linear-gradient(135deg, var(--primary-blue) 0%, #1E3A8A 100%)' : '#fff',
                                                color: isSelf ? '#fff' : '#1E293B',
                                                padding: '20px 24px',
                                                borderRadius: '24px',
                                                borderTopLeftRadius: isSelf ? '24px' : '4px',
                                                borderTopRightRadius: isSelf ? '4px' : '24px',
                                                boxShadow: isSelf ? '0 10px 15px -3px rgba(30, 58, 138, 0.2)' : '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
                                                fontSize: '15px',
                                                lineHeight: '1.7',
                                                border: isSelf ? 'none' : '1px solid #E2E8F0',
                                                position: 'relative'
                                            }}>
                                                {msg.messageText}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: isSelf ? 'var(--primary-blue)' : '#64748B',
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    {isSelf ? 'EXECUTIVE COMMAND (YOU)' : 'FIELD OPERATIVE / USER'}
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#CBD5E1' }}>•</span>
                                                <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Executive Command Input Area */}
                        {selectedTicket.status !== 'Resolved' ? (
                            <div className="modal-actions" style={{ padding: '20px 40px 28px', background: '#fff', borderTop: '1px solid #E2E8F0', boxShadow: '0 -10px 20px rgba(0,0,0,0.02)', marginTop: 0 }}>
                                <form onSubmit={handleReply} style={{ width: '100%', display: 'flex', gap: '20px', alignItems: 'center' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <input
                                            type="text"
                                            className="glass-input"
                                            placeholder="Enter administrative directive for secure transmission..."
                                            value={replyText}
                                            onChange={(e) => setReplyText(e.target.value)}
                                            style={{
                                                height: '64px',
                                                borderRadius: '20px',
                                                padding: '0 30px',
                                                fontSize: '16px',
                                                background: '#F1F5F9',
                                                border: '2px solid transparent',
                                                transition: 'all 0.3s ease'
                                            }}
                                        />
                                    </div>
                                    <button type="submit" className="action-btn-gold" style={{
                                        width: '72px',
                                        height: '64px',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px',
                                        boxShadow: '0 10px 15px -3px rgba(212, 175, 55, 0.3)',
                                        transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                    }}>
                                        <FaPaperPlane />
                                    </button>
                                    <button type="button" className="action-btn-secondary" title="Archive Mission" onClick={() => handleResolve(selectedTicket._id)} style={{
                                        width: '72px',
                                        height: '64px',
                                        borderRadius: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '24px'
                                    }}>
                                        <FaCheckDouble />
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div style={{
                                padding: '32px',
                                textAlign: 'center',
                                background: 'linear-gradient(to right, #F1F5F9, #F8FAFC, #F1F5F9)',
                                color: '#475569',
                                fontSize: '15px',
                                fontWeight: 600,
                                borderTop: '1px solid #E2E8F0',
                                letterSpacing: '0.5px'
                            }}>
                                <FaCheckDouble style={{ marginRight: '10px', color: '#10B981', fontSize: '20px' }} />
                                Operational Mission Finalized. Issue resolved and archived.
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SupportAndNotifications;

